import React, { useEffect, useState } from "react";
import Carousel from "react-multi-carousel";
import "react-multi-carousel/lib/styles.css";
import { motion } from "framer-motion";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import { Quote, Star } from "lucide-react";
import httpCommon from "../http-common";
import Container from "../component/Container";
import Social from "../component/Social";

const responsive = {
  superLargeDesktop: { breakpoint: { max: 4000, min: 3000 }, items: 2 },
  desktop: { breakpoint: { max: 3000, min: 1024 }, items: 2 },
  tablet: { breakpoint: { max: 1024, min: 464 }, items: 2 },
  mobile: { breakpoint: { max: 464, min: 0 }, items: 1 },
};

const CustomRightArrow = ({ onClick }) => (
  <button
    type="button"
    aria-label="Next testimonial"
    className="custom-arrow custom-right-arrow absolute right-0 top-1/2 z-20 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-slate-200 bg-white text-[#0F172A] shadow-md transition hover:border-[#FECE51] hover:bg-amber-50 md:right-1"
    onClick={onClick}
  >
    <IoIosArrowForward size={22} />
  </button>
);

const CustomLeftArrow = ({ onClick }) => (
  <button
    type="button"
    aria-label="Previous testimonial"
    className="custom-arrow custom-left-arrow absolute left-0 top-1/2 z-20 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-slate-200 bg-white text-[#0F172A] shadow-md transition hover:border-[#FECE51] hover:bg-amber-50 md:left-1"
    onClick={onClick}
  >
    <IoIosArrowBack size={22} />
  </button>
);

function Clients() {
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const response = await httpCommon.get("/testimonials");
      setTestimonials(response.data);
    } catch (error) {
      console.error("Error fetching testimonials", error);
    }
  };

  return (
    <section className="clients relative overflow-hidden border-t border-slate-100 bg-gradient-to-b from-[#f0f9ff]/80 via-white to-white">
      <div
        className="pointer-events-none absolute -right-20 top-0 h-72 w-72 rounded-full bg-sky-100/60 blur-3xl"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute bottom-0 left-0 h-56 w-56 rounded-full bg-[#FECE51]/15 blur-3xl"
        aria-hidden
      />

      <Container className="relative max-w-6xl px-4 py-12 md:px-6 md:py-16 lg:py-20">
        <motion.header
          className="mx-auto mb-10 max-w-2xl text-center md:mb-12"
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.45 }}
        >
          <div className="mb-3 flex justify-center">
            <span className="inline-flex items-center gap-2 rounded-full border border-slate-200/90 bg-white px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-[#727272] shadow-sm">
              <Star className="h-3.5 w-3.5 fill-[#FECE51] text-[#FECE51]" aria-hidden />
              Testimonials
            </span>
          </div>
          <h2 className="text-[#0F172A]">Our Clients Say</h2>
          <p className="mx-auto mt-2 max-w-md text-[14px] text-[#727272]">
            Real feedback from buyers and investors we&apos;ve guided.
          </p>
          <hr className="mx-auto mt-4 h-1 w-20 rounded-full bg-[#FECE51]" />
        </motion.header>

        <div className="rounded-2xl border border-slate-200/90 bg-white/90 p-3 shadow-[0_12px_40px_rgba(15,23,42,0.06)] ring-1 ring-black/[0.02] backdrop-blur-sm md:p-6 lg:p-8">
          {testimonials.length === 0 ? (
            <div className="py-16 text-center text-[14px] text-[#727272] md:py-20">
              No testimonials to show yet.
            </div>
          ) : (
            <Carousel
              swipeable={true}
              draggable={true}
              responsive={responsive}
              ssr={true}
              infinite={true}
              autoPlay={true}
              autoPlaySpeed={4500}
              keyBoardControl={true}
              customTransition="all 400ms ease-out"
              transitionDuration={400}
              containerClass="carousel-container"
              removeArrowOnDeviceType={["tablet", "mobile"]}
              dotListClass="custom-dot-list-style home-carousel-dots--listings"
              itemClass="carousel-item-padding-40-px flex items-stretch"
              customRightArrow={<CustomRightArrow />}
              customLeftArrow={<CustomLeftArrow />}
            >
              {testimonials.map((item) => (
                <div
                  className="card ml-3 flex min-h-[200px] flex-col justify-between rounded-2xl border border-slate-100 bg-gradient-to-b from-white to-slate-50/80 p-6 shadow-sm md:ml-4 md:min-h-[220px] md:p-7"
                  key={item.id}
                >
                  <Quote
                    className="h-8 w-8 text-[#FECE51]/80"
                    strokeWidth={1.25}
                    aria-hidden
                  />
                  <p className="mt-4 flex-1 text-[14px] leading-relaxed text-[#374151] md:text-[15px]">
                    {item.message}
                  </p>
                  <div className="mt-6 flex items-center gap-3 border-t border-slate-100 pt-5">
                    <div className="h-12 w-12 shrink-0 overflow-hidden rounded-full border-2 border-[#FECE51]/60 p-0.5 ring-2 ring-white shadow-sm md:h-14 md:w-14">
                      <img
                        alt=""
                        className="h-full w-full rounded-full object-cover"
                        src={
                          item.photo
                            ? `data:image/jpeg;base64,${item.photo}`
                            : "/default-profile.png"
                        }
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[15px] font-semibold text-[#0F172A]">
                        {item.name}
                      </p>
                      <p className="truncate text-[12px] text-[#727272]">
                        {item.designation}
                      </p>
                      <div className="mt-1 flex items-center gap-0.5">
                        {[...Array(Math.min(Number(item.rating) || 0, 5))].map(
                          (_, index) => (
                            <Star
                              key={index}
                              className="h-3.5 w-3.5 fill-amber-400 text-amber-400"
                              aria-hidden
                            />
                          )
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </Carousel>
          )}
        </div>
      </Container>
      <Social />
    </section>
  );
}

export default Clients;
